// Notification Sound Utility
// Uses Web Audio API to generate notification sounds with customization

let audioContext = null;

const initAudioContext = () => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioContext;
};

// Sound presets with different tones
const SOUND_PRESETS = {
  chime: {
    name: 'Chime',
    frequencies: [523.25, 659.25], // C5, E5 - major third
    type: 'sine',
    duration: 0.5,
    volume: 0.3
  },
  bell: {
    name: 'Bell',
    frequencies: [880, 1108.73], // A5, C#6 - major third higher
    type: 'sine',
    duration: 0.6,
    volume: 0.25
  },
  ping: {
    name: 'Ping',
    frequencies: [1318.51], // E6 - single high note
    type: 'sine',
    duration: 0.15,
    volume: 0.2
  },
  soft: {
    name: 'Soft',
    frequencies: [392, 523.25], // G4, C5 - perfect fourth
    type: 'sine',
    duration: 0.4,
    volume: 0.15
  },
  alert: {
    name: 'Alert',
    frequencies: [659.25, 783.99, 987.77], // E5, G5, B5 - major chord
    type: 'triangle',
    duration: 0.3,
    volume: 0.35
  },
  bubble: {
    name: 'Bubble',
    frequencies: [493.88, 659.25], // B4, E5
    type: 'sine',
    duration: 0.25,
    volume: 0.2,
    pitchBend: true
  }
};

// Get available sound presets
export const getSoundPresets = () => {
  return Object.entries(SOUND_PRESETS).map(([key, preset]) => ({
    id: key,
    name: preset.name
  }));
};

// Play a specific sound preset
export const playSound = (presetId = 'chime', volumeMultiplier = 1) => {
  try {
    const preset = SOUND_PRESETS[presetId] || SOUND_PRESETS.chime;
    const ctx = initAudioContext();
    
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const now = ctx.currentTime;
    const gainNode = ctx.createGain();
    gainNode.connect(ctx.destination);
    
    const effectiveVolume = preset.volume * volumeMultiplier;
    
    preset.frequencies.forEach((freq, index) => {
      const osc = ctx.createOscillator();
      osc.connect(gainNode);
      
      if (preset.pitchBend) {
        osc.frequency.setValueAtTime(freq * 0.8, now);
        osc.frequency.exponentialRampToValueAtTime(freq, now + 0.1);
      } else {
        osc.frequency.setValueAtTime(freq, now);
      }
      
      osc.type = preset.type;
      osc.start(now + (index * 0.02));
      osc.stop(now + preset.duration);
    });
    
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(effectiveVolume, now + 0.02);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + preset.duration);
    
    return true;
  } catch (error) {
    console.log('Could not play notification sound:', error.message);
    return false;
  }
};

// Play notification sound based on type and user preferences
export const playNotificationSound = (type = 'message', preferences = null) => {
  // Default preferences
  const defaultPrefs = {
    enabled: true,
    soundId: 'chime',
    volume: 1,
    mutedTypes: []
  };
  
  const prefs = preferences || getStoredPreferences() || defaultPrefs;
  
  // Check if sound is enabled
  if (!prefs.enabled) return false;
  
  // Check if this type is muted
  if (prefs.mutedTypes && prefs.mutedTypes.includes(type)) return false;
  
  // Play the selected sound
  return playSound(prefs.soundId || 'chime', prefs.volume || 1);
};

// Get stored preferences from localStorage
export const getStoredPreferences = () => {
  try {
    const stored = localStorage.getItem('notificationSoundPreferences');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// Save preferences to localStorage
export const savePreferences = (preferences) => {
  try {
    localStorage.setItem('notificationSoundPreferences', JSON.stringify(preferences));
    return true;
  } catch {
    return false;
  }
};

// Play a softer sound for less important notifications
export const playSoftNotificationSound = () => {
  return playSound('soft', 0.5);
};

// Preview a sound (for settings UI)
export const previewSound = (presetId) => {
  return playSound(presetId, 1);
};

// Notification types that can be muted
export const NOTIFICATION_TYPES = [
  { id: 'messages', name: 'Messages', description: 'New messages from trainer/clients' },
  { id: 'workouts', name: 'Workout Reminders', description: 'Daily workout reminders' },
  { id: 'nutrition', name: 'Nutrition Reminders', description: 'Meal logging reminders' },
  { id: 'plans', name: 'New Plans', description: 'When new plans are assigned' },
  { id: 'achievements', name: 'Achievements', description: 'PRs and milestones' }
];

export default {
  playNotificationSound,
  playSoftNotificationSound,
  playSound,
  previewSound,
  getSoundPresets,
  getStoredPreferences,
  savePreferences,
  NOTIFICATION_TYPES
};
